import { article, articleCategories, articles, articlesByTag, materialCategories, materials, tag } from './queries'
import { graphql } from '@/api/web'
import { objectHasKeys } from '@/utils/comparators'
import { NotFoundException } from '~/api/exceptions'

const actions = {
  SET_CATEGORIES: function (val) {
    this.categories = val
  },
  SET_MATERIAL_CATEGORIES: function (val) {
    this.materialCategories = val
  },
  ADD_ARTICLE: function ({ article, locale }) {
    if (!this.articles[locale]) {
      this.articles[locale] = {}
    }
    this.articles[locale][article.slug] = article
  },
  CREATE_CATEGORY: function (categoryId) {
    this.articleLists[categoryId] = {
      count: 0,
      articles: [],
      featured: []
    }
  },
  CREATE_MATERIAL_CATEGORY: function (categoryId) {
    this.materialLists[categoryId] = {
      count: 0,
      materials: []
    }
  },
  CREATE_TAG: function (val) {
    this.tags[val.slug] = {
      ...val,
      count: 0,
      articles: []
    }
  },
  ADD_ARTICLES_TO_TAG: function ({ slug, val }) {
    this.tags[slug].articles.push(...val)
  },
  SET_TAG_ARTICLES_COUNT: function ({ slug, val }) {
    this.tags[slug].count = val
  },
  ADD_ARTICLES_TO_CATEGORY: function ({ categoryId, val }) {
    this.articleLists[categoryId].articles.push(...val)
  },
  ADD_MATERIALS_TO_CATEGORY: function ({ categoryId, val }) {
    this.materialLists[categoryId].materials.push(...val)
  },
  SET_CATEGORY_ARTICLES_COUNT: function ({ categoryId, val }) {
    this.articleLists[categoryId].count = val
  },
  SET_CATEGORY_MATERIALS_COUNT: function ({ categoryId, val }) {
    this.materialLists[categoryId].count = val
  },
  SET_CATEGORY_ARTICLES_FEATURED: function ({ categoryId, val }) {
    this.articleLists[categoryId].featured.push(...val)
  },
  // #region Loaders
  loadBase: async function ({ locale }) {
    if (this.categories.length === 0 || this.materialCategories.length === 0) {
      const queries = [articleCategories(locale).build, materialCategories(locale).build]
      await this.fetchBase(queries)
    }
  },
  loadArticle: async function ({ slug, locale }) {
    await this.loadBase({ locale })
    if (!objectHasKeys(this.articles[slug])) {
      await this.fetchArticle({ slug, locale })
    }
  },
  loadArticles: async function ({ take, locale, categoryId = 'start' }) {
    if (!this.articleLists[categoryId]) {
      await this.fetchArticles({ skip: 0, locale, categoryId })
      return
    }

    const data = this.articleLists[categoryId]

    const exist = data.articles.length
    if (exist === 0 || take > exist) {
      await this.fetchArticles({ skip: exist, locale, categoryId })
    }
  },
  loadHomePage: async function ({ take, locale }) {
    await this.loadBase({ locale })
    if (!this.articleLists.start) {
      await this.fetchArticles({ skip: 0, locale, categoryId: 'start' })
      return
    }

    const data = this.articleLists.start

    const exist = data.articles.length
    if (exist === 0 || take > exist) {
      await this.fetchArticles({ skip: exist, locale, categoryId: 'start' })
    }
  },
  loadCategoryPage: async function ({ take, locale, slug }) {
    await this.loadBase({ locale })
    const categoryId = this.getCategoryBySlug(slug).id

    if (!categoryId) {
      throw new NotFoundException(`Press Category with slug ${slug} not found!`)
    }

    if (!this.articleLists[categoryId]) {
      await this.fetchArticles({ skip: 0, locale, categoryId })
      return
    }

    const data = this.articleLists[categoryId]

    const exist = data.articles.length
    if (exist === 0 || take > exist) {
      await this.fetchArticles({ skip: exist, locale, categoryId })
    }
  },
  loadMaterialCategoryPage: async function ({ take, locale, slug }) {
    await this.loadBase({ locale })
    const categoryId = this.getMaterialCategoryBySlug(slug).id

    if (!categoryId) {
      throw new NotFoundException(`Press Material Category with slug ${slug} not found!`)
    }

    if (!this.materialLists[categoryId]) {
      await this.fetchMaterials({ skip: 0, locale, categoryId })
      return
    }

    const data = this.materialLists[categoryId]

    const exist = data.materials.length
    if (exist === 0 || take > exist) {
      await this.fetchMaterials({ skip: exist, locale, categoryId })
    }
  },
  loadTagPage: async function ({ take, locale, slug }) {
    await this.loadBase({ locale })
    await this.loadTag({ slug, locale })
    const tag = this.getTagBySlug(slug)

    if (!tag) {
      throw new NotFoundException(`Press Tag with slug ${slug} not found!`)
    }

    const tagId = this.getTagBySlug(slug).id
    await this.loadArticlesByTag({ take, locale, tagId, slug })
  },
  loadArticlesByTag: async function ({ take, locale, tagId, slug }) {
    if (!this.tags[slug]) {
      this.fetchArticlesByTag({ skip: 0, locale, tagId, slug })
      return
    }

    const data = this.tags[slug]

    const exist = data.articles.length
    if (exist === 0 || take > exist) {
      await this.fetchArticlesByTag({ skip: exist, locale, tagId, slug })
    }
  },
  loadTag: async function ({ slug, locale }) {
    if (!objectHasKeys(this.tags[slug])) {
      await this.fetchTag({ slug, locale })
    }
  },
  fetchBase: async function (queries) {
    const response = await graphql(`{${queries.join(' ')}}`, 'base')
    this.SET_CATEGORIES(response.articleCategories)
    this.SET_MATERIAL_CATEGORIES(response.materialCategories)
  },
  fetchArticle: async function ({ slug, locale }) {
    const response = await graphql(`${article(locale, slug).build}`, `press press-article-${slug}`)

    if (!response.pressArticle) {
      throw new NotFoundException(`Press Article with slug ${slug} not found!`)
    }

    this.ADD_ARTICLE({ article: response.pressArticle, locale })
  },
  fetchArticles: async function ({ skip = 0, locale, categoryId }) {
    const response = await graphql(`{${articles(locale, categoryId, skip).join(' ')}}`, `press press-articles-${categoryId}`)
    if (skip === 0) {
      this.CREATE_CATEGORY(categoryId)
      this.SET_CATEGORY_ARTICLES_COUNT({ categoryId, val: response.meta.count })
      this.SET_CATEGORY_ARTICLES_FEATURED({ categoryId, val: response.featuredArticle })
    }
    this.ADD_ARTICLES_TO_CATEGORY({ categoryId, val: response.articles })
  },
  fetchMaterials: async function ({ skip = 0, locale, categoryId }) {
    const query = [
      ...materials(locale, categoryId, skip)
    ]

    const response = await graphql(`{${query.join(' ')}}`, `press press-materials-${categoryId}`)

    if (skip === 0) {
      this.CREATE_MATERIAL_CATEGORY(categoryId)
      this.SET_CATEGORY_MATERIALS_COUNT({ categoryId, val: response.meta.count })
    }

    this.ADD_MATERIALS_TO_CATEGORY({ categoryId, val: response.materials })
  },
  fetchArticlesByTag: async function ({ skip = 0, locale, tagId, slug }) {
    const response = await graphql(`{${articlesByTag(locale, tagId, skip)}}`, `press press-tag ${tagId}`)
    if (skip === 0) {
      this.SET_TAG_ARTICLES_COUNT({ slug, val: response.meta.count })
    }

    this.ADD_ARTICLES_TO_TAG({ slug, val: response.articles })
  },
  fetchTag: async function ({ slug, locale }) {
    const response = await graphql(`{${tag(locale, slug).build}}`, `press press-tag ${slug}`)

    if (!response.tags) {
      throw new NotFoundException(`Press Tag with slug ${slug} not found!`)
    }

    if (response.tags.length > 0) {
      this.CREATE_TAG(response.tags[0])
    }
  }
}

// #endregion

export default actions
